import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { Box, Drawer, useTheme } from '@mui/material';
import { motion, useMotionValue, animate } from 'framer-motion';
import { glassStyles } from '../../tokens/designTokens';
import { isCapacitorNative } from '../../utils/platformUtils';
import { registerSheet } from '../../utils/bottomSheetBackStack';

// Framer Motion spring for the slide-up panel.
// The glass (backdrop-filter) is on the inner motion panel, NOT on the MUI
// Paper. Putting backdrop-filter on the same element as a CSS transform
// (which MUI Drawer uses for its built-in Slide animation) causes a known
// WebKit compositor conflict where the filter doesn't render during the
// animation. By separating the two — transparent Paper for portal/backdrop/
// focus-trap, animated MUI Box for visual styling — both work correctly on iOS.
//
// MotionBox = motion.create(Box) so the panel still accepts MUI `sx`
// shorthands (bgcolor, etc.) and inherits glass tokens from designTokens.ts.
const MotionBox = motion.create(Box);

const SPRING_IN   = { type: 'spring' as const, stiffness: 380, damping: 32, mass: 0.8 };
const SPRING_BACK = { type: 'spring' as const, stiffness: 600, damping: 50, mass: 0.5 };

const DRAG_CLOSE_OFFSET   = 100; // px dragged down
const DRAG_CLOSE_VELOCITY = 500; // px/s flick speed

// ─── Drag-zone context ────────────────────────────────────────────────────────
// Provides a single `onPointerDown` that sheets can attach to their header.
// Document-level move/up listeners are wired inside `startDrag` so they fire
// regardless of where the finger travels — no setPointerCapture required.

interface DragZoneHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
}

const BottomSheetDragContext = React.createContext<DragZoneHandlers | null>(null);

/**
 * Wrap a sheet's title/header area with this component so it becomes a
 * pull-to-close drag zone. Must render *inside* the BottomSheet's children
 * so the context is available. The close IconButton still works — taps with
 * no movement are ignored.
 */
export const BottomSheetDraggableHeader: React.FC<{
  children: React.ReactNode;
  sx?: object;
}> = ({ children, sx }) => {
  const ctx = useContext(BottomSheetDragContext);
  return (
    <Box
      onPointerDown={ctx?.onPointerDown}
      sx={{
        ...(ctx ? { touchAction: 'none', cursor: 'grab', '&:active': { cursor: 'grabbing' } } : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Max height of the sheet panel. Defaults to '90dvh'. */
  maxHeight?: string;
  /** Fixed height (for full-screen sheets like WriteReviewDialog). */
  height?: string;
  /** Lifts the sheet above the keyboard. Pass `${keyboardHeight}px` when keyboard is open. */
  bottom?: string | number;
  /** z-index override (e.g. for nested sheets). */
  zIndex?: number;
  /** Extra ModalProps (e.g. disableEnforceFocus for keyboard-aware dialogs). */
  modalProps?: {
    keepMounted?: boolean;
    disableEnforceFocus?: boolean;
    disableAutoFocus?: boolean;
    disableRestoreFocus?: boolean;
  };
  /**
   * Called when the user drags upward past the sheet's resting position by 80px+.
   * Opt-in — only MovieQuickViewSheet uses this to navigate to the full movie page.
   * Other sheets are unaffected.
   */
  onSwipeUpPastMax?: () => void;
  /**
   * Set true for sheets that manage their own browser history (e.g. MovieQuickViewSheet
   * via React Router navigate). Prevents BottomSheet from pushing a duplicate history
   * entry on web, which would require two back gestures to close.
   */
  disableWebHistoryBack?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

// Counts programmatic history.go(-1) calls triggered by sheet cleanup so the
// resulting popstate events are not mistaken for user back-gestures by sibling
// sheets that are still open.
let programmaticGoBackCount = 0;

const SWIPE_UP_THRESHOLD = -80; // px above resting position to trigger onSwipeUpPastMax

const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  children,
  maxHeight = '90dvh',
  height,
  bottom,
  zIndex,
  modalProps,
  onSwipeUpPastMax,
  disableWebHistoryBack = false,
}) => {
  const isDark = useTheme().palette.mode === 'dark';

  // Single motion value drives enter, drag, snap-back, and exit.
  const y = useMotionValue(800);

  // Slide up whenever the sheet opens.
  useEffect(() => {
    if (!open) return;
    y.set(800);
    const ctrl = animate(y, 0, SPRING_IN);
    return () => ctrl.stop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Stable refs so closures always see the latest callbacks.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const onSwipeUpPastMaxRef = useRef(onSwipeUpPastMax);
  useEffect(() => { onSwipeUpPastMaxRef.current = onSwipeUpPastMax; }, [onSwipeUpPastMax]);

  // Capacitor back-button: register with the global sheet stack so
  // useSwipeBackGesture closes this sheet instead of navigating away.
  useEffect(() => {
    if (!open) return;
    return registerSheet(() => onCloseRef.current());
  }, [open]);

  // Web browser back gesture: push a history entry when the sheet opens so the
  // browser's edge-swipe/back-button pops it and closes the sheet rather than
  // navigating away. Skip for sheets that manage their own history (MovieQuickViewSheet).
  useEffect(() => {
    if (!open || isCapacitorNative() || disableWebHistoryBack) return;

    let closedByPopState = false;
    window.history.pushState({ _bs: true }, '');

    const handlePopState = () => {
      if (programmaticGoBackCount > 0) {
        programmaticGoBackCount--;
        return;
      }
      closedByPopState = true;
      onCloseRef.current();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Sheet closed programmatically (not by back gesture) — remove the entry we pushed.
      if (!closedByPopState) {
        programmaticGoBackCount++;
        window.history.go(-1);
      }
    };
  // disableWebHistoryBack is intentionally excluded — it's a static prop and we don't
  // want to re-run this effect if it somehow changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Attaches document-level pointermove/pointerup listeners so the gesture
   * stays alive no matter where the finger travels. No setPointerCapture
   * needed — and no React synthetic-event delegation issues.
   *
   * A 5 px threshold before we start moving prevents accidental drags on taps,
   * keeping child click handlers (close buttons etc.) fully functional.
   */
  const startDrag = useCallback((e: React.PointerEvent) => {
    const startY  = e.clientY;
    let lastY     = startY;
    let lastTime  = Date.now();
    let velocity  = 0;
    let started   = false; // true once we've moved > 5 px

    const cleanup = () => {
      document.removeEventListener('pointermove',   onMove);
      document.removeEventListener('pointerup',     onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };

    const onMove = (ev: PointerEvent) => {
      const delta = ev.clientY - startY;

      if (!started) {
        if (Math.abs(delta) < 5) return;
        started = true;
      }

      // Swipe-up-past-max: only when the opt-in callback is provided.
      if (onSwipeUpPastMaxRef.current && delta < SWIPE_UP_THRESHOLD) {
        cleanup();
        onSwipeUpPastMaxRef.current();
        return;
      }

      const now = Date.now();
      const dt  = now - lastTime;
      if (dt > 0) velocity = (ev.clientY - lastY) / dt * 1000;
      lastY    = ev.clientY;
      lastTime = now;
      // Allow upward drag only when onSwipeUpPastMax is provided; otherwise clamp at 0.
      y.set(onSwipeUpPastMaxRef.current ? delta : Math.max(0, delta));
    };

    const onUp = async () => {
      cleanup();

      if (!started) return; // was a tap — let child click fire normally

      const offset = Math.max(0, y.get());

      if (offset > DRAG_CLOSE_OFFSET || velocity > DRAG_CLOSE_VELOCITY) {
        await animate(y, 800, { duration: 0.2, ease: [0.4, 0, 1, 1] });
        onCloseRef.current();
      } else {
        animate(y, 0, SPRING_BACK);
      }
    };

    document.addEventListener('pointermove',   onMove);
    // Capture phase so the Drawer's onPointerUp stopPropagation (bubble phase)
    // can't block our listener from firing.
    document.addEventListener('pointerup',     onUp, true);
    document.addEventListener('pointercancel', onUp, true);
  }, [y]);

  /**
   * Universal pull-down-to-close for the content area (not just the pill handle).
   * Fires on pointerdown anywhere inside the sheet body. Guards:
   *   1. Text input focused → do nothing (protects WriteReviewDialog etc.)
   *   2. Scrollable ancestor not at top → do nothing (let content scroll)
   *   3. Determines direction from the first 8px of movement before committing:
   *      - Downward → delegate to startDrag (close gesture)
   *      - Upward   → abort (let browser scroll into content)
   */
  const handleContentDrag = useCallback((e: React.PointerEvent) => {
    // Guard 1: text input focused
    const focused = document.activeElement;
    if (focused instanceof HTMLElement &&
        (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.isContentEditable)) return;

    // Guard 2: a scrollable ancestor is not at the top
    let el = e.target as HTMLElement;
    const boundary = e.currentTarget as HTMLElement;
    while (el && el !== boundary) {
      if (el.scrollHeight > el.clientHeight && el.scrollTop > 0) return;
      el = el.parentElement as HTMLElement;
    }

    // Direction detection — wait for first 8px before deciding.
    // If upward → abort (let browser scroll into content).
    // If downward → delegate to startDrag (close gesture).
    const startY = e.clientY;
    let decided = false;

    const onPrelimMove = (ev: PointerEvent) => {
      const delta = ev.clientY - startY;
      if (Math.abs(delta) < 8) return;
      decided = true;
      document.removeEventListener('pointermove', onPrelimMove);
      document.removeEventListener('pointerup',   onPrelimCancel, true);
      document.removeEventListener('pointercancel', onPrelimCancel, true);

      if (delta > 0) {
        // Downward — commit to close gesture.
        // Re-fire startDrag with the original event so it can track from startY.
        startDrag(e);
      }
      // Upward — do nothing; browser handles scroll normally.
    };

    const onPrelimCancel = () => {
      if (!decided) {
        document.removeEventListener('pointermove', onPrelimMove);
        document.removeEventListener('pointerup',   onPrelimCancel, true);
        document.removeEventListener('pointercancel', onPrelimCancel, true);
      }
    };

    document.addEventListener('pointermove',   onPrelimMove);
    document.addEventListener('pointerup',     onPrelimCancel, true);
    document.addEventListener('pointercancel', onPrelimCancel, true);
  }, [startDrag]);

  const dragZone: DragZoneHandlers = { onPointerDown: startDrag };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      // Both 0 — we own all animation on the MotionBox.
      transitionDuration={{ enter: 0, exit: 0 }}
      sx={zIndex != null ? { zIndex } : undefined}
      ModalProps={{ keepMounted: false, ...modalProps }}
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          ...(bottom != null ? { bottom } : {}),
        },
      }}
    >
      <MotionBox
        style={{ y }}
        sx={{
          ...(height != null ? { height } : {}),
          maxHeight: height != null ? height : maxHeight,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          touchAction: 'manipulation',
          ...glassStyles.bottomSheet(isDark),
        }}
      >
        <BottomSheetDragContext.Provider value={dragZone}>
          {/* Content wrapper: enables pull-down-to-close from the body when at scrollTop=0. */}
          <Box onPointerDown={handleContentDrag} sx={{ display: 'contents' }}>
            {children}
          </Box>
        </BottomSheetDragContext.Provider>
      </MotionBox>
    </Drawer>
  );
};

export default BottomSheet;
